import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { Skeleton } from "./Estados.js";
export function DataTable({ columns, rows, rowKey, onRowClick, emptyState, loading, actions, topScrollbar = true, caption }) {
    const topRef = useRef(null);
    const bottomRef = useRef(null);
    const tableRef = useRef(null);
    const [innerWidth, setInnerWidth] = useState(0);
    const [hasOverflow, setHasOverflow] = useState(false);
    // Mede a largura real do <table> vs o container. So renderiza a barra
    // top quando ha overflow horizontal — tabelas que cabem no viewport
    // continuam identicas ao layout antigo. Reage a resize dos dois lados
    // (viewport shrink OU coluna nova depois do mount).
    //
    // A medicao NAO depende mais de `topScrollbar` (feature 008): `hasOverflow`
    // passou a decidir tambem se o quadro entra na ordem de tabulacao, e uma
    // tabela com a barra de cima desligada transborda do mesmo jeito.
    useEffect(() => {
        const t = tableRef.current;
        const b = bottomRef.current;
        if (!t || !b)
            return;
        const measure = () => {
            const sw = t.scrollWidth;
            const cw = b.clientWidth;
            setInnerWidth(sw);
            setHasOverflow(sw > cw + 1);
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(t);
        ro.observe(b);
        return () => ro.disconnect();
    }, [columns.length, rows.length]);
    // Sincroniza scrollLeft entre top e bottom sem loop infinito (flag por-frame).
    useEffect(() => {
        if (!topScrollbar || !hasOverflow)
            return;
        const top = topRef.current;
        const bot = bottomRef.current;
        if (!top || !bot)
            return;
        let lock = false;
        const onTop = () => {
            if (lock)
                return;
            lock = true;
            bot.scrollLeft = top.scrollLeft;
            requestAnimationFrame(() => { lock = false; });
        };
        const onBot = () => {
            if (lock)
                return;
            lock = true;
            top.scrollLeft = bot.scrollLeft;
            requestAnimationFrame(() => { lock = false; });
        };
        top.addEventListener("scroll", onTop, { passive: true });
        bot.addEventListener("scroll", onBot, { passive: true });
        return () => {
            top.removeEventListener("scroll", onTop);
            bot.removeEventListener("scroll", onBot);
        };
    }, [topScrollbar, hasOverflow]);
    const showTop = topScrollbar && hasOverflow;
    if (loading) {
        // Esqueleto com a altura de algumas linhas, e nao um "Carregando..." que
        // troca de tamanho quando o dado chega (feature 008, FR-035).
        return (_jsx("div", { "aria-busy": "true", style: { padding: 16, border: "1px solid var(--border)", borderRadius: 12 }, children: _jsx(Skeleton, { linhas: 4, altura: 18 }) }));
    }
    if (rows.length === 0) {
        return (_jsx("div", { style: { padding: 36, textAlign: "center", color: "var(--text-muted)", border: "1px solid var(--border)", borderRadius: 12 }, children: emptyState ?? "Nenhum item encontrado." }));
    }
    return (_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 0 }, children: [showTop ? (_jsx("div", { ref: topRef, className: "atlas-datatable-topscroll", "aria-hidden": "true", tabIndex: -1, style: {
                    overflowX: "auto",
                    overflowY: "hidden",
                    border: "1px solid var(--border)",
                    borderBottom: "none",
                    borderRadius: "12px 12px 0 0",
                    // Altura enxuta pra so mostrar a barra estilizada (12px em webkit).
                    height: 16,
                }, children: _jsx("div", { style: { width: innerWidth || "100%", height: 1 } }) })) : null, _jsx("div", { ref: bottomRef, className: "atlas-datatable-scroll", 
                // ROLAGEM PRESA NO QUADRO, E ALCANCAVEL PELO TECLADO (feature 008, FR-039).
                // Uma regiao que rola e nao recebe foco deixa o conteudo da direita
                // inacessivel para quem nao usa mouse — e' o que a regra
                // `scrollable-region-focusable` do axe acusa como `serious`. So' quando ha
                // transbordo de verdade: uma tabela que cabe na tela nao vira parada de
                // Tab a toa.
                tabIndex: hasOverflow ? 0 : undefined, style: {
                    overflowX: "auto",
                    border: "1px solid var(--border)",
                    borderRadius: showTop ? "0 0 12px 12px" : 12,
                    borderTop: showTop ? "none" : undefined,
                }, children: _jsxs("table", { ref: tableRef, style: { width: "100%", borderCollapse: "collapse", fontSize: 13 }, children: [caption ? (_jsx("caption", { style: { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }, children: caption })) : null, _jsx("thead", { children: _jsxs("tr", { style: { background: "var(--bg-elev-2)" }, children: [columns.map((c) => (_jsx("th", { style: {
                                            textAlign: c.align ?? "left",
                                            padding: "10px 12px",
                                            fontSize: 11,
                                            fontWeight: 700,
                                            letterSpacing: "0.06em",
                                            textTransform: "uppercase",
                                            color: "var(--text-dim)",
                                            whiteSpace: "nowrap",
                                            width: c.width,
                                        }, children: c.header }, c.key))), actions ? _jsx("th", { style: { width: 1 } }) : null] }) }), _jsx("tbody", { children: rows.map((r) => (_jsxs("tr", { onClick: onRowClick ? () => onRowClick(r) : undefined, style: {
                                    borderTop: "1px solid var(--border)",
                                    cursor: onRowClick ? "pointer" : "default",
                                }, onMouseEnter: (e) => {
                                    if (onRowClick)
                                        e.currentTarget.style.background = "var(--bg-elev-2)";
                                }, onMouseLeave: (e) => {
                                    e.currentTarget.style.background = "transparent";
                                }, children: [columns.map((c) => (_jsx("td", { style: {
                                            padding: "10px 12px",
                                            textAlign: c.align ?? "left",
                                            fontFamily: c.mono ? "var(--font-mono)" : undefined,
                                            color: "var(--text)",
                                            whiteSpace: c.wrap ? "normal" : "nowrap",
                                            // overflowWrap: quebra so quando a palavra e' longa demais pra
                                            // a coluna. Diferente de wordBreak:break-word que quebra em
                                            // qualquer lugar (parte "ESTATUTARIO" em "ESTATUTARI/O").
                                            overflowWrap: c.wrap ? "anywhere" : undefined,
                                            maxWidth: c.wrap ? 320 : undefined,
                                        }, children: c.render ? c.render(r) : r[c.key] }, c.key))), actions ? (_jsx("td", { style: { padding: "10px 12px", whiteSpace: "nowrap", textAlign: "right" }, onClick: (e) => e.stopPropagation(), children: actions(r) })) : null] }, rowKey(r)))) })] }) })] }));
}
export function IconButton({ children, onClick, title, danger }) {
    return (_jsx("button", { type: "button", onClick: onClick, title: title, style: {
            width: 30,
            height: 30,
            borderRadius: 8,
            border: "1px solid var(--border-strong)",
            background: "transparent",
            color: danger ? "var(--danger-500)" : "var(--text-muted)",
            cursor: "pointer",
            marginLeft: 4,
        }, children: children }));
}
//# sourceMappingURL=DataTable.js.map